'use client'

import { useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { HelpNavCategory, HelpNavLink } from '@/lib/help-nav'

// Client island: the docs left-nav tree. Categories are collapsible,
// and a category can have a second collapsible level (sub-groups) for
// when it holds many articles. The category (and group) containing the
// current article start expanded. The active article link gets
// aria-current="page" and a lime accent. Everything is keyboard-
// navigable (native links + button toggles).
export function HelpSidebar({ nav }: { nav: HelpNavCategory[] }) {
  const pathname = usePathname()
  const currentSlug = pathname?.startsWith('/help/') ? pathname.slice('/help/'.length) : null

  const categoryHasSlug = (c: HelpNavCategory, slug: string | null) =>
    !!slug &&
    (c.articles.some((a) => a.slug === slug) || c.groups.some((g) => g.articles.some((a) => a.slug === slug)))

  const activeCategory = nav.find((c) => categoryHasSlug(c, currentSlug))?.slug ?? null

  // Default expanded categories: the active one, or all of them on the
  // index page (so the reader sees the full map).
  const [openCat, setOpenCat] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    for (const c of nav) init[c.slug] = activeCategory ? c.slug === activeCategory : true
    return init
  })

  // Default expanded sub-groups: only the group containing the active
  // article. Keyed as `${categorySlug}::${groupTitle}`.
  const [openGroup, setOpenGroup] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    for (const c of nav) {
      for (const g of c.groups) {
        const key = `${c.slug}::${g.title}`
        init[key] = g.articles.some((a) => a.slug === currentSlug)
      }
    }
    return init
  })

  const toggleCat = (slug: string) => setOpenCat((o) => ({ ...o, [slug]: !o[slug] }))
  const toggleGroup = (key: string) => setOpenGroup((o) => ({ ...o, [key]: !o[key] }))

  const linkClass = (active: boolean) =>
    cn(
      'block rounded-md px-3 py-1.5 leading-snug transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
      active
        ? 'bg-primary/10 font-medium text-primary'
        : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground',
    )

  const renderLinks = (links: HelpNavLink[]) =>
    links.map((a) => {
      const active = a.slug === currentSlug
      return (
        <li key={a.slug}>
          <Link href={`/help/${a.slug}`} aria-current={active ? 'page' : undefined} className={linkClass(active)}>
            {a.title}
          </Link>
        </li>
      )
    })

  return (
    <nav aria-label="Help topics" className="text-sm">
      <Link
        href="/help"
        aria-current={pathname === '/help' ? 'page' : undefined}
        className={cn(
          'mb-3 block rounded-md px-3 py-1.5 font-display font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
          pathname === '/help'
            ? 'bg-primary/10 text-primary'
            : 'text-foreground hover:bg-secondary/60',
        )}
      >
        Help center home
      </Link>

      <ul className="space-y-1">
        {nav.map((c) => {
          const isOpen = openCat[c.slug]
          const hasActive = c.slug === activeCategory
          return (
            <li key={c.slug}>
              <button
                type="button"
                onClick={() => toggleCat(c.slug)}
                aria-expanded={isOpen}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-md px-3 py-1.5 text-left font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                  hasActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/40',
                )}
              >
                <span className="font-display">{c.title}</span>
                <ChevronRight
                  className={cn('h-4 w-4 shrink-0 transition-transform', isOpen && 'rotate-90')}
                  aria-hidden="true"
                />
              </button>

              <div
                className={cn(
                  'grid transition-[grid-template-rows] duration-200 ease-out',
                  isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                )}
              >
                <div className="overflow-hidden">
                  <div className="mt-0.5 border-l border-border/70 pl-3">
                    {c.articles.length > 0 && (
                      <ul className="space-y-0.5">{renderLinks(c.articles)}</ul>
                    )}

                    {c.groups.map((g) => {
                      const key = `${c.slug}::${g.title}`
                      const gOpen = openGroup[key]
                      const gActive = g.articles.some((a) => a.slug === currentSlug)
                      return (
                        <div key={key} className={cn(c.articles.length > 0 && 'mt-1.5')}>
                          <button
                            type="button"
                            onClick={() => toggleGroup(key)}
                            aria-expanded={gOpen}
                            className={cn(
                              'flex w-full items-center justify-between gap-2 rounded-md px-3 py-1 text-left text-xs font-medium uppercase tracking-wide transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                              gActive ? 'text-foreground' : 'text-muted-foreground/80 hover:text-foreground hover:bg-secondary/40',
                            )}
                          >
                            <span>{g.title}</span>
                            <ChevronRight
                              className={cn('h-3.5 w-3.5 shrink-0 transition-transform', gOpen && 'rotate-90')}
                              aria-hidden="true"
                            />
                          </button>
                          <div
                            className={cn(
                              'grid transition-[grid-template-rows] duration-200 ease-out',
                              gOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                            )}
                          >
                            <div className="overflow-hidden">
                              <ul className="mt-0.5 space-y-0.5 border-l border-border/60 pl-2">{renderLinks(g.articles)}</ul>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
